import { reportDate, useReportClock } from '../../reportClock';
import React, { Fragment, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import differenceBy from 'lodash/differenceBy';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { FormGroup, Row, Col, Card, CardBody, InputGroup, InputGroupText } from 'reactstrap';
import { FileText } from 'react-feather';
import { Typeahead } from 'react-bootstrap-typeahead';
import { H2, H6, P, Breadcrumbs } from '../../../../AbstractElements';
import { firebase_app, firebase_rdb } from '../../../../Config/Config';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import i18n from '../../../../i18n';
import { formatEventDateTime } from '../../../../utils/eventTruth';
import AvisDetailModal from '../AvisDetailModal';

const CLIENT_ID = 'maxpark-220731';

const paginationComponentOptions = {
  rowsPerPageText: 'Avis par page',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
  selectAllRowsItemText: 'Tous',
};

export const formatPaymentStatus = isPaid => {
  if (isPaid) {
    return 'Payé';
  }

  return 'Non payé';
};

export const getAvisDatePickerDisplayOptions = () => ({ showWeekNumbers: false });

const getClientRef = () => firebase_rdb.child('clients').child(CLIENT_ID);

const getAuthenticatedUser = () => new Promise(resolve => {
  const currentUser = firebase_app.auth().currentUser;

  if (currentUser) {
    resolve(currentUser);
    return;
  }

  let resolved = false;
  const unsubscribe = firebase_app.auth().onAuthStateChanged(user => {
    if (resolved) {
      return;
    }

    resolved = true;
    unsubscribe();
    resolve(user);
  });

  setTimeout(() => {
    if (resolved) {
      return;
    }

    resolved = true;
    unsubscribe();
    resolve(firebase_app.auth().currentUser);
  }, 1500);
});

const getLegacyAgentIds = async () => {
  const agentsRef = getClientRef().child('agents');
  const currentUser = await getAuthenticatedUser();

  if (currentUser && typeof fetch === 'function') {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${agentsRef.toString()}.json?shallow=true&auth=${encodeURIComponent(token)}`);

      if (response.ok) {
        const agents = await response.json();
        return Object.keys(agents || {});
      }
    } catch (error) {
      console.warn('Unable to read shallow agent list; falling back to SDK.', error);
    }
  }

  const agentsSnapshot = await agentsRef.once('value');
  const agentIds = [];

  agentsSnapshot.forEach(agentSnapshot => {
    agentIds.push(agentSnapshot.key);
    return false;
  });

  return agentIds;
};

const normalizePayment = payment => {
  if (payment && typeof payment === 'object') {
    return {
      isPaid: payment.paid === true,
      paidprice: payment.paidprice || '',
      paiddate: payment.paymentdate || '',
    };
  }

  return {
    isPaid: false,
    paidprice: '',
    paiddate: '',
  };
};

const buildTicketRow = ticket => {
  if (!ticket || !ticket.ticketid) {
    return null;
  }

  const payment = normalizePayment(ticket.payment);
  const carPlate = ticket.carplate ? ticket.carplate.replace(' ', '').replace('-', '') : '';

  return {
    avis: ticket.ticketid,
    client: ticket.clientname || '',
    contractid: ticket.contract || '',
    contractname: ticket.contractname || '',
    marque: ticket.carbrand || '',
    modele: ticket.carmodel || '',
    plaque: carPlate,
    agent: ticket.agent || '',
    date: ticket.date || '',
    datetime: ticket.datetime || '',
    time: ticket.time || '',
    paiement: payment.isPaid,
    paidprice: payment.paidprice,
    paiddate: payment.paiddate,
    status: ticket.status || '',
    prix: ticket.price || '',
  };
};

const addTicketIfInRange = (ticketsById, ticket, startDate, endDate) => {
  if (!ticket || !ticket.date || ticket.date < startDate || ticket.date > endDate) {
    return;
  }

  const row = buildTicketRow(ticket);
  if (row && !ticketsById.has(row.avis)) {
    ticketsById.set(row.avis, row);
  }
};

const addTicketsFromSnapshot = (ticketsById, snapshot, startDate, endDate) => {
  snapshot.forEach(ticketSnapshot => {
    addTicketIfInRange(ticketsById, ticketSnapshot.val(), startDate, endDate);
    return false;
  });
};

const sortTicketsNewestFirst = tickets => tickets.sort((left, right) => {
  const leftDate = left.datetime || `${left.date} ${left.time}`;
  const rightDate = right.datetime || `${right.date} ${right.time}`;
  return rightDate.localeCompare(leftDate);
});

export const loadTicketsForRange = async (pStartDate, pEndDate) => {
  const ticketsById = new Map();
  const clientRef = getClientRef();

  try {
    const centralSnapshot = await clientRef
      .child('tickets')
      .orderByChild('date')
      .startAt(pStartDate)
      .endAt(pEndDate)
      .once('value');

    addTicketsFromSnapshot(ticketsById, centralSnapshot, pStartDate, pEndDate);
  } catch (error) {
    console.warn('Central ticket query failed; trying legacy agent tickets.', error);
  }

  try {
    const agentIds = await getLegacyAgentIds();

    await Promise.all(agentIds.map(async agentId => {
      try {
        const ticketsSnapshot = await clientRef
          .child('agents')
          .child(agentId)
          .child('tickets')
          .orderByChild('date')
          .startAt(pStartDate)
          .endAt(pEndDate)
          .once('value');

        addTicketsFromSnapshot(ticketsById, ticketsSnapshot, pStartDate, pEndDate);
      } catch (error) {
        console.warn('Legacy agent ticket query failed.', error);
      }
    }));
  } catch (error) {
    console.warn('Legacy agent list query failed; returning central tickets only.', error);
  }

  return sortTicketsNewestFirst(Array.from(ticketsById.values()));
};

const AvisList = () => {
  const [modal, setModal] = useState(false);
  const [modalID, setModalID] = useState(0);
  const toggle = () => setModal(!modal);

  const [ticketData, setTicketData] = useState([]);
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [noTickets, setNoTickets] = useState(0);
  const [filters, setFilters] = useState([]);

  const requestId = useRef(0);
  const reportNow = useReportClock();
  const todayDate = reportDate(reportNow).replace(/-/g, '/');
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [rangeDates, setRangeDates] = useState('');

  useEffect(() => {
    initTable(rangeDates || undefined, true);
    return () => { requestId.current += 1; };
  }, [reportNow]);

  const handleDateChange = date => {
    setDateRange(date || [null, null]);

    if (!date) {
      setRangeDates('');
      initTable();
      return;
    }

    if (date[1] != null) {
      const datePickerDates = {
        startDate: date[0],
        endDate: date[1],
      };

      setRangeDates(datePickerDates);
      initTable(datePickerDates);
    }
  };

  const handleRowSelected = useCallback(state => {
    setSelectedRows(state.selectedRows);
  }, []);

  const contextActions = useMemo(() => {
    const handleDelete = () => {
      if (window.confirm(`${i18n.t('Dashboard_Avis_DeleteConfirm')}\r ${selectedRows.map(r => r.name)}?`)) {
        setToggleCleared(!toggleCleared);
        setData(differenceBy(data, selectedRows, 'name'));
        toast.success(i18n.t('Dashboard_Avis_DeleteSuccess'));
      }
    };

    return <button key="delete" className="btn btn-danger" onClick={handleDelete}>{i18n.t('Dashboard_Common_Delete')}</button>;
  }, [data, selectedRows, toggleCleared]);

  async function initTable(pDateOptions, silent = false) {
    const request = ++requestId.current;
    const pStartDate = pDateOptions
      ? format(new Date(pDateOptions.startDate), 'yyyy/MM/dd')
      : todayDate;
    const pEndDate = pDateOptions
      ? format(new Date(pDateOptions.endDate), 'yyyy/MM/dd')
      : todayDate;

    try {
      const dataTickets = await loadTicketsForRange(pStartDate, pEndDate);
      if (request !== requestId.current) return;
      const preFilterArray = [];

      dataTickets.forEach(ticket => {
        preFilterArray.push(ticket.agent);
        preFilterArray.push(ticket.contractname);
      });

      const filteredArray = removeDuplicates(preFilterArray)
        .filter(Boolean)
        .map(item => ({ agent: item }));

      setFilters(filteredArray);
      setNoTickets(dataTickets.length);
      setTicketData(dataTickets);

      if (dataTickets.length === 0 && !silent) {
        toast.warning(`Aucun avis trouvé(s) ! Pour la période début : ${pStartDate} fin: ${pEndDate}`);
      }
    } catch (error) {
      if (request !== requestId.current) return;
      console.error('Erreur de chargement des avis', error);
      setNoTickets(0);
      setTicketData([]);
      setFilters([]);
      toast.error('Impossible de charger les avis. Veuillez réessayer.');
    }
  }

  function removeDuplicates(arr) {
    return arr.filter((item, index) => arr.indexOf(item) === index);
  }

  const handleFilterChange = e => {
    if (e) {
      const searchText = e.agent.toLowerCase();
      const searchData = ticketData.filter(item => (
        item.agent.toString().toLowerCase().includes(searchText)
        || item.client.toString().toLowerCase().includes(searchText)
        || item.contractname.toString().toLowerCase().includes(searchText)
      ));

      setTicketData(searchData);
    } else {
      initTable(rangeDates);
    }
  };

  function showModal(avis_id) {
    setModalID(avis_id);
    setModal(!modal);
  }

  function downloadXLS() {
    const csvHeaders = [
      'Avis',
      'Client',
      'Contrat',
      'Contrat ID',
      'Plaque',
      'Agent',
      'Date',
      'Heure',
      i18n.t('Dashboard_Avis_Status'),
      'Paiement',
      'Date de paiement',
    ];

    const csvRows = ticketData.map(ticket => [
      ticket.avis,
      ticket.client,
      ticket.contractname,
      ticket.contractid,
      ticket.plaque,
      ticket.agent,
      ticket.date,
      ticket.time,
      ticket.status,
      ticket.paiement ? ticket.paidprice : '-',
      ticket.paiement ? ticket.paiddate : '-',
    ]);

    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(value => `"${String(value || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `avis-${todayDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const tableColumns = [
    {
      name: 'Avis',
      selector: row => (
        <>
          <button onClick={() => showModal(row.avis)} className='bg-transparent border-0 cursor-pointer bg-green text-green-600 fill-green stroke-green px-2 mx-2'>
            <i className="fa fa-plus hover:fa-green "></i>
          </button>
          <Link to={`${process.env.PUBLIC_URL}/dashboard/avis/${row.avis}`}>{row.avis}</Link>
        </>
      ),
      accessor: 'link',
      sortable: true,
      center: false,
      when: row => row.paiement === true,
      style: row => ({ borderLeft: row.paiement === true ? '1px solid red' : '' }),
    },
    { name: 'Client', selector: row => <div>{row.client}</div>, sortable: true, center: false },
    { name: 'Contrat', selector: row => row.contractname, sortable: true, center: false },
    {
      name: 'Contrat ID',
      selector: row => row.contractid,
      sortable: true,
      center: false,
      width: '115px',
      wrap: true,
      style: { whiteSpace: 'normal', overflowWrap: 'anywhere' },
    },
    {
      name: 'Plaque',
      selector: row => row.plaque,
      sortable: true,
      center: false,
      width: '115px',
      wrap: true,
      style: { whiteSpace: 'normal', overflowWrap: 'anywhere' },
    },
    { name: 'Agent', selector: row => row.agent, sortable: true, center: false },
    {
      name: 'Date et heure',
      selector: row => formatEventDateTime(row.date, row.time || row.datetime) || '-',
      sortable: true,
      sortFunction: (left, right) => (
        formatEventDateTime(left.date, left.time || left.datetime)
          .localeCompare(formatEventDateTime(right.date, right.time || right.datetime))
      ),
      center: false,
      minWidth: '170px',
      maxWidth: '190px',
    },
    { name: i18n.t('Dashboard_Avis_Status'), selector: row => row.status, sortable: true, center: false },
    { name: 'Paiement', selector: row => formatPaymentStatus(row.paiement), sortable: true, center: false },
    { name: 'Date de paiement', selector: row => row.paiement ? row.paiddate : '-', sortable: true, center: false },
  ];

  return (
    <Fragment>
      <Row>
        <Col sm="12" md="12">
          <Breadcrumbs mainTitle={i18n.t('Dashboard_Common_Admin')} parent={i18n.t('Dashboard_Common_Dashboard')} title="Avis" />
          <Card>
            <CardBody>
              <Row className="">
                <Col md="8">
                  <H2 attrH6={{ className: 'title' }}>Liste des avis</H2>
                  <P>Cette liste contient tous les avis émis par les agents.</P>
                </Col>
                <Col md="4">
                  <button type='button' className='btn btn-secondary btn-block pull-right' onClick={downloadXLS}>
                    <FileText className="h-15" />
                    Exporter {noTickets} avis
                  </button>
                </Col>
              </Row>

              <Row className="m-t-50">
                <Col md="8">
                  <H6 attrH6={{ className: 'sub-title' }}>Filtres</H6>
                  <P>Contrat et/ou Agent</P>
                  <div id="prefetch">
                    <FormGroup className="mb-0">
                      <InputGroup className="pill-input-group">
                        <InputGroupText><i className="fa fa-search"></i></InputGroupText>
                        <Typeahead
                          id="multiple-typeahead"
                          clearButton
                          labelKey={'agent'}
                          multiple={false}
                          options={filters}
                          placeholder="Entrez un filtre"
                          onChange={e => handleFilterChange(e[0])}
                        />
                      </InputGroup>
                    </FormGroup>
                  </div>
                </Col>
                <Col md="4">
                  <div className="daterange-card">
                    <H6 attrH6={{ className: 'sub-title' }}>Période</H6>
                    <P>Sélectionnez la période pour les avis.</P>
                    <div className="theme-form">
                      <FormGroup>
                        <InputGroup className="pill-input-group">
                          <InputGroupText><i className="fa fa-calendar"></i></InputGroupText>
                          <DatePicker
                            {...getAvisDatePickerDisplayOptions()}
                            className="form-control digits"
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleDateChange}
                            isClearable={true}
                            dropdownMode="select"
                            dateFormat="yyyy/MM/dd/"
                            todayButton="Aujourd'hui"
                            calendarStartDay={1}
                            placeholderText={`${todayDate} - ${todayDate}`}
                          />
                        </InputGroup>
                      </FormGroup>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <DataTable
                    className="table-avis-list m-t-20"
                    data={ticketData}
                    columns={tableColumns}
                    striped={true}
                    center={true}
                    noHeader
                    pagination
                    paginationPerPage={100}
                    paginationRowsPerPageOptions={[100, 200, 400, 500]}
                    paginationComponentOptions={paginationComponentOptions}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <AvisDetailModal
        avisId={modalID}
        isOpen={modal}
        toggle={toggle}
      />
    </Fragment>
  );
};

export default AvisList;
